import React from 'react';
import { AIMessagesBanner } from 'components/AI/AIBanner/AIMessagesBanner/AIMessagesBanner';
import { AIFilesBanner } from 'components/AI/AIBanner/AIFilesBanner/AIFilesBanner';
import { Stack } from '@mui/material';
import { AIFileReview } from 'components/AI/AIFileReview/AIFileReview';
import { useAiEnabled } from 'hooks/ai-firm-settings-hooks';
import { useFlags } from 'launchdarkly-react-client-sdk';

export interface AIFlaggedBannerProps {
  /**
   * Type of banner to display
   */
  type: 'message' | 'file';
  /**
   * Old ugly dependency to refresh the list in parent component
   */
  component: any;
}

/**
 * AIFlaggedBanner component that conditionally renders based on feature flags
 *
 * @param props - Component props
 * @returns React component or null if feature flag is disabled
 */
export const AIFlaggedBanner: React.FC<AIFlaggedBannerProps> = ({
  type,
  component,
}) => {
  const { isAiEnabled } = useAiEnabled();
  const { enableAiTagging, enableAiFileRenaming } = useFlags();

  // Return null if the feature flag is disabled
  if (!isAiEnabled) {
    return null;
  }

  if (type === 'file' && !(enableAiFileRenaming || enableAiTagging)) {
    return null;
  }

  // Return the appropriate banner based on type
  const banner = type === 'message' ? <AIMessagesBanner /> : <AIFilesBanner />;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={4}
      sx={{ padding: type === 'message' ? '0 0 1.6rem 0' : '1.6rem 2.3rem' }}
    >
      <Stack sx={{ flex: 1 }}>{banner}</Stack>
      {type === 'file' && <AIFileReview component={component} />}
    </Stack>
  );
};

export default AIFlaggedBanner;
